<script lang="ts">
import { defineComponent, PropType } from 'vue'
import apiRequestManager from '@/_core/apiRequestManager'
import { STORIES, KEYWORDS } from '@/_core/endpoint'
import TOAST_MESSAGE_TYPES from '@/constants/toastMessageTypes'
import ButtonDefault from '@/components/button/ButtonDefault.vue'
import { newUserSessionPath } from '@/generated/railsRoutes'
import { useUserStore } from '@/stores/user'
import { useToastStore } from '@/stores/toast'

export default defineComponent({
  name: 'ButtonFollow',

  components: {
    ButtonDefault,
  },

  props: {
    /**
     * フォロー対象の種別
     * @description story: 特集 / keyword: 用語
     */
    type: {
      type: String as PropType<'story' | 'keyword'>,
      default: 'story',
    },

    /**
     * フォロー対象の ID
     * @description story: 特集ID / keyword: キーワードID
     */
    targetId: {
      type: Number,
      required: true,
    },

    /**
     * フォロー中かどうか
     */
    isActive: {
      type: Boolean,
      required: true,
    },
  },

  emits: ['update:follow'],

  setup(props, { emit }) {
    // store
    const userStore = useUserStore()
    const toastStore = useToastStore()

    // フォロー切替エンドポイント（種別ごと。story=POST / keyword=PUT の verb は各 endpoint が保持）
    const ENDPOINTS = {
      story: STORIES.FOLLOW,
      keyword: KEYWORDS.FOLLOW,
    }

    /**
     * リクエスト処理中かどうか
     * @description 実操作では endpoint 側の screenLock（STORIES.FOLLOW / KEYWORDS.FOLLOW ともに
     *              hasLockScreen: true）がクリック直後に全画面を遮蔽するため、連打はそもそも届かない。
     *              このフラグはその設定に依存しないための二次防御。follow API は1リクエストごとの
     *              トグルのため、多重発行するとサーバ状態とローカル反転した表示が食い違う。
     */
    let inFlight = false

    /**
     * クリック時のフォロー状態切替
     * @description フォローは非同期ジョブで処理され、レスポンス本文には依存しない
     *              （keyword follow は 204 No Content）。成功時はローカルで状態を反転する。
     */
    const onClick = async () => {
      if (inFlight) return

      // 未ログインの場合はログイン画面に遷移する
      if (!userStore.context.isSignedIn) return

      const config = ENDPOINTS[props.type]
      const path = config.path({ id: props.targetId })
      const endpoint = { ...config, path }
      const nextActive = !props.isActive

      try {
        inFlight = true
        await apiRequestManager.handleRequest({
          endpoint,
          callback: (_response: unknown, isSuccess: boolean) => {
            if (isSuccess) {
              emit('update:follow', nextActive)
            }
            handleToastMessage(isSuccess, nextActive)
          }
        })
      } catch (error) {
        console.error(error)
        handleToastMessage(false, props.isActive)
      } finally {
        inFlight = false
      }
    }

    function handleToastMessage(isSuccess: boolean, isActive: boolean) {
      const id = `toast-${new Date().getTime()}`
      const messageType: MessageType = isSuccess
        ? TOAST_MESSAGE_TYPES.SUCCESS as MessageType
        : TOAST_MESSAGE_TYPES.ALERT as MessageType

      const messageSuffix = isActive
        ? 'しました。'
        : '解除しました。'

      const message = isSuccess
        ? `フォロー${messageSuffix}`
        : 'フォロー状態の更新に失敗しました。'

      toastStore.setToastItem({
        id,
        messageType,
        message,
        duration: 4,
      })
    }

    return {
      // routes
      newUserSessionPath,

      // store
      userStore,

      // methods
      onClick,
    }
  }
})
</script>

<template>
  <ButtonDefault
    :is-link="!userStore.context.isSignedIn"
    :href="!userStore.context.isSignedIn ? newUserSessionPath() : undefined"
    is-font-small
    has-shadow
    emit-name="click:follow"
    :data-active="isActive"
    @click:follow="onClick">
    {{ isActive ? 'フォロー中' : 'フォローする' }}
  </ButtonDefault>
</template>

<style lang="sass" scoped>
.o-action
  $root: &

  &[data-active="true"]
    ::v-deep(#{$root}--item)
      color: $font-orange
      background-color: rgba($bg-xx-light-orange, 1)
      border-color: $border-orange
</style>
